import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, ShieldAlert, RefreshCcw, Copy } from "lucide-react";
import { toast } from "sonner";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api } from "@/lib/api";

export default function AdminQuestionnairesPage() {
  const [params] = useSearchParams();
  const token = params.get("t") || params.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [responses, setResponses] = useState([]);

  const fetch = () => {
    setLoading(true);
    setError("");
    api
      .get(`/admin/questionnaires`, { params: { token } })
      .then((r) => {
        setResponses(r.data.responses || []);
        setLoading(false);
      })
      .catch((e) => {
        setError(e?.response?.data?.detail || "Failed to fetch responses.");
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError("Missing token. Append ?t=<ADMIN_TOKEN> to the URL.");
      return;
    }
    fetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const copyAll = () => {
    const text = responses
      .map((r) => {
        const answerText = Object.entries(r.answers || {})
          .map(([k, v]) => `  ${k}:\n    ${v || "(no answer)"}`)
          .join("\n");
        return `FROM: ${r.submitter_name} <${r.submitter_email}>\nSUBMITTED: ${r.created_at}\n${"-".repeat(48)}\n${answerText}`;
      })
      .join("\n\n" + "=".repeat(48) + "\n\n");
    navigator.clipboard.writeText(text);
    toast.success("Copied all responses to clipboard.");
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <section className="bg-purple-dark text-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-14">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Admin · Internal
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight">
            Questionnaire responses
          </h1>
          <p className="text-white/75 text-sm mt-3">
            All client questionnaire submissions. Sorted newest first.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 py-12">
        {loading ? (
          <div className="flex items-center gap-3 text-purple" data-testid="admin-q-loading">
            <Loader2 className="animate-spin" size={20} /> Loading...
          </div>
        ) : error ? (
          <div
            className="border border-red-300 bg-red-50 text-red-900 p-6 flex items-start gap-3"
            data-testid="admin-q-error"
          >
            <ShieldAlert size={20} className="mt-0.5" />
            <div>
              <div className="font-bold mb-1">Access denied</div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <div className="text-sm text-ink/70" data-testid="admin-q-count">
                <strong className="text-purple-dark">{responses.length}</strong>{" "}
                total submission{responses.length === 1 ? "" : "s"}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={copyAll}
                  className="inline-flex items-center gap-2 border border-purple text-purple hover:bg-grey-light px-4 py-2 rounded-sm text-sm font-bold transition-colors"
                  data-testid="admin-q-copy-all"
                >
                  <Copy size={14} /> Copy all
                </button>
                <button
                  onClick={fetch}
                  className="inline-flex items-center gap-2 bg-purple text-white hover:bg-purple-dark px-4 py-2 rounded-sm text-sm font-bold transition-colors"
                  data-testid="admin-q-refresh"
                >
                  <RefreshCcw size={14} /> Refresh
                </button>
              </div>
            </div>

            {responses.length === 0 ? (
              <div className="border border-grey bg-grey-light/50 p-10 text-center text-ink/60 text-sm">
                No submissions yet.
              </div>
            ) : (
              <div className="space-y-6">
                {responses.map((r, i) => (
                  <div
                    key={i}
                    className="border border-grey bg-white"
                    data-testid={`admin-q-response-${i}`}
                  >
                    <div className="bg-purple-dark text-white px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div>
                        <div className="h-serif text-lg">{r.submitter_name}</div>
                        <a
                          href={`mailto:${r.submitter_email}`}
                          className="text-gold-light text-xs hover:underline"
                        >
                          {r.submitter_email}
                        </a>
                      </div>
                      <div className="text-xs text-white/70">
                        {new Date(r.created_at).toLocaleString()}
                      </div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {Object.entries(r.answers || {}).map(([k, v]) => (
                          <tr key={k} className="border-b border-grey last:border-none">
                            <td className="align-top text-purple font-bold text-xs uppercase tracking-widest p-4 w-56 border-r border-grey">
                              {k}
                            </td>
                            <td className="p-4 whitespace-pre-wrap text-ink/90">
                              {v || <span className="italic text-ink/40">(no answer)</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <SiteFooter />
    </div>
  );
}
